import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// UI components
import Card from '../../components/ui/Card';

// Mock data
import { services } from '../../data/mockData';

/**
 * Services overview page
 */
const ServicesPage: React.FC = () => {
  return (
    <Layout>
      {/* Hero section */}
      <Section bgColor="bg-primary" padding="lg" className="text-white">
        <Container>
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">Our AI Services</h1>
            <p className="text-lg md:text-xl text-white/80 mb-8">
              Comprehensive AI solutions designed to transform your business, from initial training to advanced custom development.
            </p>
          </div>
        </Container>
      </Section>

      {/* Services grid */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex"
                >
                  <Link to={service.url} className="w-full h-full">
                    <Card hover className="h-full flex flex-col">
                      <div className="p-6 md:p-8 flex-grow">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <div className="ml-4">
                            <h2 className="text-xl font-semibold">{service.title}</h2>
                            {service.badge && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary mt-1">
                                {service.badge}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-neutral-700 mb-6">{service.description}</p>
                        
                        <div className="mt-auto pt-2">
                          <span className="inline-flex items-center text-primary font-medium">
                            Learn more
                            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* How we approach AI */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Our Approach to AI</h2>
              <p className="text-lg text-neutral-700">
                At CodemyAI, we believe that successful AI implementation requires a thoughtful, human-centered approach.
              </p>
            </div>
            
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-white rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-semibold mb-3">1. Understanding Your Business</h3>
                  <p className="text-neutral-700">
                    We begin every engagement by deeply understanding your business objectives, challenges, and existing processes. This foundation ensures that our AI solutions directly address your specific needs rather than being generic implementations.
                  </p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="bg-white rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-semibold mb-3">2. Human-Centered Design</h3>
                  <p className="text-neutral-700">
                    We design AI solutions with human users in mind. Our focus is on creating intuitive experiences that augment human capabilities rather than replacing them, leading to higher adoption rates and greater business impact.
                  </p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="bg-white rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-semibold mb-3">3. Ethical & Responsible Implementation</h3>
                  <p className="text-neutral-700">
                    We prioritize ethical considerations in all our AI implementations, ensuring transparency, fairness, and respect for privacy. Our solutions are designed to be explainable and accountable, building trust with both employees and customers.
                  </p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="bg-white rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-semibold mb-3">4. Continuous Improvement</h3>
                  <p className="text-neutral-700">
                    AI implementation is a journey, not a destination. We provide ongoing monitoring, optimization, and iteration of our solutions to ensure they continue to deliver value as your business evolves and AI technologies advance.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Call to action */}
      <Section bgColor="bg-primary" padding="lg" className="text-white">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Business with AI?</h2>
            <p className="text-lg text-white/80 mb-8">
              Schedule a complimentary consultation to discuss how our AI services can address your specific business challenges.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-neutral-100 transition-colors"
            >
              Book Your Free Consultation
            </Link>
          </div>
        </Container>
      </Section>
    </Layout>
  );
};

export default ServicesPage;